import { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { svgPoint } from './useSvgPointer';
import { nearestOrbit } from './layout';
import { G_DISC, P_DISC } from './constants';
import type { Selection } from './types';
import type { GroupEntry } from './layout';
import type { DragPreviewHandle } from './components/DragPreview';

const MOVE_THRESHOLD = 7;
const NOOP = () => {};

interface UseEditDragArgs {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  layout: GroupEntry[];
  editMode: boolean;
  svgRef: React.RefObject<SVGSVGElement | null>;
  sunX?: number;
  sunY?: number;
  onActivate?: (selection: Selection) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface DragState { [k: string]: any }

export function useEditDrag({ dispatch, layout, editMode, svgRef, sunX, sunY, onActivate = NOOP }: UseEditDragArgs) {
  const dragRef = useRef<DragState | null>(null);
  const dragPreviewRef = useRef<DragPreviewHandle | null>(null);
  const lastClickRef = useRef<{ key: string; t: number } | null>(null);
  const [liveDragPosition, setLiveDragPosition] = useState<{ kind: string; gi?: number; pi?: number; x: number; y: number } | null>(null);
  const [liveResize, setLiveResize] = useState<{ target: string; gi?: number; pi?: number; value: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!editMode || e.button !== 0) return;
    const svg = svgRef.current;
    if (!svg) return;
    const p = svgPoint(e, svg);
    const target = e.target as Element;

    const personNode = target.closest('.node');
    if (personNode) {
      e.preventDefault();
      dragRef.current = { type: 'person', gi: +personNode.dataset.gi!, pi: +personNode.dataset.pi!, ki: +personNode.dataset.ki!, node: personNode, moved: false, origin: p, target: null };
      try { svg.setPointerCapture(e.pointerId); } catch { /* */ }
      return;
    }
    const discNode = target.closest('.pnode');
    if (discNode) {
      e.preventDefault?.();
      const kind = discNode.dataset.type!;
      const gi = +discNode.dataset.gi!, pi = +discNode.dataset.pi!;
      let base: { x: number; y: number };
      if (kind === 'sun') base = { x: sunX!, y: sunY! };
      else if (kind === 'group') base = { x: layout[gi].gcx, y: layout[gi].gcy };
      else base = { x: layout[gi].progs[pi].pcx, y: layout[gi].progs[pi].pcy };
      dragRef.current = { type: 'disc', kind, gi, pi, node: discNode, moved: false, origin: p, base };
      try { svg.setPointerCapture(e.pointerId); } catch { /* */ }
      return;
    }
    const handleEl = target.closest('.hdl');
    if (handleEl) {
      e.preventDefault?.();
      dragRef.current = { type: 'resize', target: handleEl.dataset.target, gi: handleEl.dataset.gi ? +handleEl.dataset.gi : undefined, pi: handleEl.dataset.pi ? +handleEl.dataset.pi : undefined, moved: false, origin: p };
      try { svg.setPointerCapture(e.pointerId); } catch { /* */ }
      return;
    }
  }, [editMode, svgRef, layout, sunX, sunY]);

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const svg = svgRef.current;
    if (!svg) return;
    const p = svgPoint(e, svg);
    if (!drag.moved) {
      if (Math.hypot(p.x - drag.origin.x, p.y - drag.origin.y) < MOVE_THRESHOLD) return;
      drag.moved = true;
      if (drag.node) drag.node.classList.add('dragging');
    }
    if (drag.type === 'person') {
      let t = nearestOrbit(p, layout);
      if (t && e.shiftKey) {
        t.ang = Math.round(t.ang / 15) * 15;
        const rad = (t.ang * Math.PI) / 180;
        t.x = t.pe.pcx + t.pe.ring * Math.cos(rad);
        t.y = t.pe.pcy + t.pe.ring * Math.sin(rad);
      }
      drag.target = t;
      if (dragPreviewRef.current) {
        if (t) { dragPreviewRef.current.show(t, t.gi !== drag.gi || t.pi !== drag.pi, t.pe.prog.short); }
        else { dragPreviewRef.current.clear(); }
      }
      return;
    }
    if (drag.type === 'disc') {
      let nx = drag.base.x + (p.x - drag.origin.x);
      let ny = drag.base.y + (p.y - drag.origin.y);
      if (e.shiftKey) { nx = Math.round(nx / 10) * 10; ny = Math.round(ny / 10) * 10; }
      drag.liveX = nx; drag.liveY = ny;
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const d = dragRef.current;
          if (d && d.type === 'disc') {
            const pos: { kind: string; gi?: number; pi?: number; x: number; y: number } = { kind: d.kind, x: d.liveX, y: d.liveY };
            if (d.kind === 'group') pos.gi = d.gi;
            else if (d.kind === 'prog') { pos.gi = d.gi; pos.pi = d.pi; }
            flushSync(() => setLiveDragPosition(pos));
          }
        });
      }
      return;
    }
    if (drag.type === 'resize') {
      if (!drag.moved) return;
      const t = drag.target;
      let value: number;
      if (t === 'sun') value = Math.hypot(p.x - (sunX ?? 0), p.y - (sunY ?? 0));
      else if (t === 'gdisc' || t === 'gring') { const ge = layout[drag.gi]; value = Math.hypot(p.x - ge.gcx, p.y - ge.gcy); }
      else { const pe = layout[drag.gi].progs[drag.pi]; value = Math.hypot(p.x - pe.pcx, p.y - pe.pcy); }
      drag.liveValue = value;
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const d = dragRef.current;
          if (d && d.type === 'resize') setLiveResize({ target: d.target, gi: d.gi, pi: d.pi, value: d.liveValue });
        });
      }
    }
  }, [layout, svgRef, sunX, sunY]);

  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    const svg = svgRef.current;
    if (svg) { try { svg.releasePointerCapture(e.pointerId); } catch { /* */ } }
    if (dragPreviewRef.current) dragPreviewRef.current.clear();
    if (drag.node) drag.node.classList.remove('dragging');
    if (!drag.moved) {
      let selection: Selection = null;
      if (drag.type === 'person') { selection = { kind: 'person', gi: drag.gi, pi: drag.pi, ki: drag.ki }; dispatch({ type: 'SELECT', selection }); }
      else if (drag.type === 'disc') {
        selection = drag.kind === 'sun' ? { kind: 'sun' } : drag.kind === 'group' ? { kind: 'group', gi: drag.gi } : { kind: 'prog', gi: drag.gi, pi: drag.pi };
        dispatch({ type: 'SELECT', selection });
      }
      if (selection) {
        const key = JSON.stringify(selection);
        const now = Date.now();
        const last = lastClickRef.current;
        const isDoubleClick = !!last && last.key === key && now - last.t < 450;
        lastClickRef.current = isDoubleClick ? null : { key, t: now };
        if (isDoubleClick) onActivate(selection);
      }
      return;
    }
    if (drag.type === 'person') {
      const t = drag.target;
      if (!t) return;
      if (t.gi !== drag.gi || t.pi !== drag.pi) { dispatch({ type: 'REASSIGN_PERSON', gi: drag.gi, pi: drag.pi, ki: drag.ki, targetGi: t.gi, targetPi: t.pi, ang: t.ang }); }
      else { dispatch({ type: 'SET_PERSON_ANGLE', gi: drag.gi, pi: drag.pi, ki: drag.ki, ang: t.ang }); dispatch({ type: 'SELECT', selection: { kind: 'person', gi: drag.gi, pi: drag.pi, ki: drag.ki } }); }
      return;
    }
    if (drag.type === 'disc') {
      setLiveDragPosition(null);
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (drag.kind === 'sun') { dispatch({ type: 'SET_DISC_POSITION', kind: 'sun', x: drag.liveX, y: drag.liveY }); dispatch({ type: 'SELECT', selection: { kind: 'sun' } }); }
      else if (drag.kind === 'group') { dispatch({ type: 'SET_DISC_POSITION', kind: 'group', gi: drag.gi, x: drag.liveX, y: drag.liveY }); dispatch({ type: 'SELECT', selection: { kind: 'group', gi: drag.gi } }); }
      else { const ge = layout[drag.gi]; const pang = (Math.atan2(drag.liveY - ge.gcy, drag.liveX - ge.gcx) * 180) / Math.PI; const pR = Math.hypot(drag.liveX - ge.gcx, drag.liveY - ge.gcy); dispatch({ type: 'SET_DISC_POSITION', kind: 'prog', gi: drag.gi, pi: drag.pi, pang, pR }); dispatch({ type: 'SELECT', selection: { kind: 'prog', gi: drag.gi, pi: drag.pi } }); }
      return;
    }
    if (drag.type === 'resize') {
      setLiveResize(null);
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      let min: number | undefined, max: number | undefined;
      if (drag.target === 'gring') { min = (layout[drag.gi].discR ?? G_DISC) + 12; max = 520; }
      else if (drag.target === 'pring') { min = (layout[drag.gi].progs[drag.pi].discR ?? P_DISC) + 12; max = 460; }
      dispatch({ type: 'SET_SIZE', target: drag.target, gi: drag.gi, pi: drag.pi, value: drag.liveValue, min, max });
    }
  }, [dispatch, layout, onActivate]);

  const onPointerCancel = useCallback(() => {
    const drag = dragRef.current;
    if (drag) {
      if (drag.node) drag.node.classList.remove('dragging');
      dragRef.current = null;
      if (dragPreviewRef.current) dragPreviewRef.current.clear();
      if (drag.type === 'disc') { setLiveDragPosition(null); if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; } }
      else if (drag.type === 'resize') { setLiveResize(null); if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; } }
    }
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, dragPreviewRef, liveDragPosition, liveResize };
}
